
import {Injectable, NgZone} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {ScriptLoaderService} from '../script-loader-service.service';
import {Observable, Subject} from 'rxjs';
import {finalize, take} from 'rxjs/operators';

declare const FB: any;

export interface FacebookAuthOptions {
    appId: string;
    buttonId: string;
    scope?: string;
    version?: string;
}
export interface FacebookAuthResult {
    token?: string;
    error?: unknown;
}

@Injectable({providedIn: 'root'})
export class FacebookAuthService {
    private initialized = false;
    private facebookApi: any | undefined;
    private facebookSdkPromise: Promise<any> | undefined;
    private authInProgress = false;
    private buttonElement?: HTMLElement;
    private buttonHandler?: EventListener;
    private currentOptions?: FacebookAuthOptions;
    private resultSubject = new Subject<FacebookAuthResult>();

    constructor(
        private readonly scriptLoaderService: ScriptLoaderService,
        private readonly authenticationService: AuthenticationService,
        private readonly ngZone: NgZone
    ) {}

    initialize(options: FacebookAuthOptions): Observable<FacebookAuthResult> {
        if (!options.appId) {
            console.warn('Facebook sign-in disabled: missing appId.');
            return this.resultSubject.asObservable();
        }

        this.currentOptions = options;

        if (this.initialized) {
            return this.resultSubject.asObservable();
        }

        if (!this.facebookSdkPromise) {
            this.facebookSdkPromise = this.scriptLoaderService.loadFacebookScript()
                .then(() => this.waitForFacebookSdk());
        }

        this.facebookSdkPromise
            .then((facebook) => {
                this.facebookApi = facebook;
                this.configureFacebook();
            })
            .catch((error) => {
                console.error('Facebook SDK failed to load', error);
                this.emitError(error);
            });

        return this.resultSubject.asObservable();
    }

    public login(): void {
        // 1. TENTATIVE SYNCHRONE
        const fbGlobal = (window as any).FB;
        if (fbGlobal) {
            this.startLogin(fbGlobal);
            return;
        }

        // 2. TENTATIVE ASYNCHRONE (Fallback)
        if (this.facebookSdkPromise) {
            this.facebookSdkPromise.then((facebook) => {
                this.startLogin(facebook);
            });
        } else {
            console.error('Facebook Auth not initialized: missing appId');
        }
    }

    dispose(): void {
        if (this.buttonElement && this.buttonHandler) {
            this.buttonElement.removeEventListener('click', this.buttonHandler);
        }
        this.buttonElement = undefined;
        this.buttonHandler = undefined;
        this.authInProgress = false;
        this.initialized = false;
    }

    private configureFacebook(): void {
        const options = this.currentOptions;
        const facebook = (window as any).FB;

        if (!options) {
            this.emitError(new Error('Facebook sign-in options missing.'));
            return;
        }
        if (!facebook || typeof facebook.init !== 'function') {
            this.emitError(new Error('Facebook SDK not available.'));
            return;
        }

        facebook.init({
            appId: options.appId,
            cookie: true,
            xfbml: true,
            version: options.version || 'v20.0'
        });

       const element = document.getElementById(options.buttonId);
        if (element instanceof HTMLElement) {
            this.buttonElement = element;
            if (!element.dataset.facebookBound) {
                this.buttonHandler = () => this.startLogin(facebook);
                element.dataset.facebookBound = '1';
            }
        }

        this.initialized = true;
    }

    private waitForFacebookSdk(): Promise<any> {
        return new Promise((resolve, reject) => {
            const maxAttempts = 40;
            let attempts = 0;

            const check = () => {
                const facebook = (window as any).FB;
                if (facebook && typeof facebook.init === 'function') {
                    resolve(facebook);
                    return;
                }

                attempts += 1;
                if (attempts >= maxAttempts) {
                    reject(new Error('Facebook SDK not available.'));
                    return;
                }

                setTimeout(check, 100);
            };

            check();
        });
    }

    private startLogin(facebook: any): void {
        if (!this.currentOptions) {
            this.emitError(new Error('Facebook sign-in options missing.'));
            return;
        }

        if (this.authInProgress) {
            console.warn('Facebook sign-in already processing.');
            return;
        }

        this.authInProgress = true;

        facebook.login(
            (response: any) => this.handleLoginResponse(response),
            {scope: this.currentOptions?.scope || 'email,public_profile'}
        );
    }

    private handleLoginResponse(response: any): void {
        this.ngZone.run(() => {
            const authResponse = response?.authResponse;
            if (!authResponse || !authResponse.accessToken) {
                this.emitError(new Error('Facebook login failed or was cancelled.'));
                this.authInProgress = false;
                return;
            }

            this.authenticationService
                .loginWithFacebook(authResponse.accessToken)
                .pipe(
                    take(1),
                    finalize(() => {
                        this.authInProgress = false;
                    })
                )
                .subscribe({
                    next: (result) => {
                        if (result?.token) {
                            this.resultSubject.next({token: result.token});
                        } else {
                            this.emitError(new Error('Facebook login: backend returned no token'));
                        }
                    },
                    error: (error) => this.emitError(error)
                });
        });
    }

    private emitError(error: unknown): void {
        console.error('Facebook sign-in error', error);
        this.resultSubject.next({error});
    }
}
